import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from "./Sidebar";
import * as XLSX from 'xlsx';
import { 
  Search, 
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Edit2,
  Trash2,
  X,
  Save,
  Mail,
  Calendar,
  Download,
  User,
  Hash,
  Plus,
  Upload
} from 'lucide-react';

const ManageEmployees = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [updatingEmployee, setUpdatingEmployee] = useState(null);
  const navigate = useNavigate();

  // Modal states
  const [isAddEmployeeModalOpen, setIsAddEmployeeModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [newEmployee, setNewEmployee] = useState({
    name: "",
    email: "",
    employeeID: ""
  });
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [bulkEmployees, setBulkEmployees] = useState("");
  const [addEmployeeError, setAddEmployeeError] = useState("");
  const [isAddingEmployee, setIsAddingEmployee] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isSuccessOverlayOpen, setIsSuccessOverlayOpen] = useState(false);
  const [excelFile, setExcelFile] = useState(null);

  const API_BASE_URL = "https://njs-01.optimuslab.space/api/visits/employees";

  // Get current user info
  const currentUser = JSON.parse(localStorage.getItem('user'));
  const isAdmin = currentUser?.role === "Admin";

  // Logout function
  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("access_token");
    navigate("/");
  };

  // Fetch all employees
  const fetchEmployees = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(API_BASE_URL);

      if (!response.ok) {
        throw new Error('Failed to fetch employees');
      }

      const data = await response.json();
      setEmployees(data);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setError('Failed to load employees. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Add single employee
  const handleAddEmployee = async (e) => {
    e.preventDefault();
    if (!newEmployee.name.trim() || !newEmployee.email.trim() || !newEmployee.employeeID.trim()) {
      setAddEmployeeError("All fields are required");
      return;
    }

    setIsAddingEmployee(true);
    setAddEmployeeError("");

    try {
      const response = await fetch(API_BASE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify([newEmployee]),
      });

      if (response.ok) {
        const createdEmployees = await response.json();
        setEmployees([...employees, ...createdEmployees]);
        setNewEmployee({ name: "", email: "", employeeID: "" });
        setIsAddEmployeeModalOpen(false);
        
        setSuccessMessage("Employee added successfully!");
        setIsSuccessOverlayOpen(true);
        setTimeout(() => {
          setIsSuccessOverlayOpen(false);
          setSuccessMessage("");
        }, 3000);
      } else {
        const errorData = await response.json();
        setAddEmployeeError(errorData.message || "Failed to add employee");
      }
    } catch (error) {
      console.error("Error adding employee:", error);
      setAddEmployeeError("An error occurred while adding the employee");
    } finally {
      setIsAddingEmployee(false);
    }
  };

// Handle Excel file upload
// Handle Excel file upload
const handleExcelUpload = async (e) => {
    e.preventDefault();
    
    if (!excelFile) {
      setAddEmployeeError("Please select an Excel file");
      return;
    }
  
    try {
      setIsAddingEmployee(true);
      setAddEmployeeError("");
  
      const data = await excelFile.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
  
      // Skip first row if it looks like headers
      const startRow = jsonData[0] && typeof jsonData[0][0] === 'string' && 
                       (jsonData[0][0].toLowerCase().includes('name') || 
                        jsonData[0][0].toLowerCase().includes('employee')) ? 1 : 0;
  
      const employeesArray = [];
      for (let i = startRow; i < jsonData.length; i++) {
        const row = jsonData[i];
        
        if (!row || row.length === 0 || !row[0]) continue;
        
        if (!row[0] || !row[1] || !row[2]) {
          throw new Error(`Row ${i + 1} is missing required data. Expected: Name, Employee ID, Email`);
        }
        
        employeesArray.push({
          name: String(row[0]).trim(),
          employeeID: String(row[1]).trim(),
          email: String(row[2]).trim().toLowerCase() // Normalize email to lowercase
        });
      }
  
      if (employeesArray.length === 0) {
        throw new Error("No valid employee data found in the Excel file");
      }
  
      // Log what we're sending
      console.log("Sending employees:", employeesArray);
  
      const response = await fetch(API_BASE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(employeesArray),
      });
  
      if (response.ok) {
        const createdEmployees = await response.json();
        setEmployees([...employees, ...createdEmployees]);
        setExcelFile(null);
        setIsBulkUploadModalOpen(false);
        
        setSuccessMessage(`${createdEmployees.length} employee(s) added successfully!`);
        setIsSuccessOverlayOpen(true);
        setTimeout(() => {
          setIsSuccessOverlayOpen(false);
          setSuccessMessage("");
        }, 3000);
      } else {
        // Get detailed error message from server
        let errorMessage = "Failed to add employees";
        try {
          const errorData = await response.json();
          console.error("Server error:", errorData);
          errorMessage = errorData.message || errorData.error || JSON.stringify(errorData);
        } catch (e) {
          const errorText = await response.text();
          console.error("Server error text:", errorText);
          errorMessage = errorText || errorMessage;
        }
        setAddEmployeeError(errorMessage);
      }
    } catch (error) {
      console.error("Error in Excel upload:", error);
      setAddEmployeeError(error.message || "Invalid Excel format or error occurred");
    } finally {
      setIsAddingEmployee(false);
    }
  };



  // Edit employee
  const handleEditEmployee = async (e) => {
    e.preventDefault();
    if (!editingEmployee) return;

    setUpdatingEmployee(editingEmployee._id);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/${editingEmployee._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: editingEmployee.name,
          email: editingEmployee.email,
          employeeID: editingEmployee.employeeID
        }),
      });

      if (response.ok) {
        const updatedEmployee = await response.json();
        setEmployees(employees.map(emp => 
          emp._id === updatedEmployee._id ? updatedEmployee : emp
        ));
        setIsEditModalOpen(false);
        setEditingEmployee(null);
        
        setSuccessMessage("Employee updated successfully!");
        setIsSuccessOverlayOpen(true);
        setTimeout(() => {
          setIsSuccessOverlayOpen(false);
          setSuccessMessage("");
        }, 3000);
      } else {
        setError("Failed to update employee");
      }
    } catch (error) {
      console.error("Error updating employee:", error);
      setError("An error occurred while updating the employee");
    } finally {
      setUpdatingEmployee(null);
    }
  };

  const handleExportToExcel = () => {
    try {
      const dataToExport = filteredEmployees.length > 0 ? filteredEmployees : employees;
      
      if (dataToExport.length === 0) {
        alert("No employees to export");
        return;
      }
  
      // Prepare data matching the template format
      const excelData = dataToExport.map((employee) => ({
        'Employee Name': employee.name,
        'Employee ID': employee.employeeID,
        'Email Address': employee.email
      }));
  
      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      
      // Set column widths
      worksheet['!cols'] = [
        { wch: 25 },  // Employee Name
        { wch: 15 },  // Employee ID
        { wch: 35 }   // Email Address
      ];
  
      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Employees');
  
      // Generate filename
      const date = new Date().toISOString().split('T')[0];
      const filename = `Employees_${date}.xlsx`;
  
      // Download file
      XLSX.writeFile(workbook, filename);
  
      setSuccessMessage(`Exported ${dataToExport.length} employee(s) successfully!`);
      setIsSuccessOverlayOpen(true);
      setTimeout(() => {
        setIsSuccessOverlayOpen(false);
        setSuccessMessage("");
      }, 3000);
  
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      setError("Failed to export employees to Excel");
    }
  };

  // Delete employee
  const handleDeleteEmployee = async (employeeId, employeeName) => {
    if (!window.confirm(`Are you sure you want to delete ${employeeName}?`)) {
      return;
    }

    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/${employeeId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        setEmployees(employees.filter(emp => emp._id !== employeeId));
        
        setSuccessMessage("Employee deleted successfully!");
        setIsSuccessOverlayOpen(true);
        setTimeout(() => {
          setIsSuccessOverlayOpen(false);
          setSuccessMessage("");
        }, 3000);
      } else {
        setError("Failed to delete employee");
      }
    } catch (error) {
      console.error("Error deleting employee:", error);
      setError("An error occurred while deleting the employee");
    }
  };

  // Filter employees based on search
  const filteredEmployees = employees.filter(employee => {
    const matchesSearch = 
      employee.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      employee.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      employee.employeeID.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesSearch;
  });

  useEffect(() => {
    if (isAdmin) {
      fetchEmployees();
    }
  }, []);

  // Access control
  if (!currentUser || currentUser.role !== "Admin") {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6">You don't have permission to access this page.</p>
          <button
            onClick={() => navigate('/invite')}
            className="px-6 py-2 bg-[#ce5841] text-white rounded-lg font-medium hover:bg-orange-700 transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Sidebar onLogout={handleLogout} />
        <div className="ml-32 mr-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center space-x-2">
              <RefreshCw className="w-6 h-6 animate-spin text-[#ce5841]" />
              <span className="text-gray-600">Loading employees...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar onLogout={handleLogout} />

     

      {/* Add Employee Modal */}
      {isAddEmployeeModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-40" onClick={() => setIsAddEmployeeModalOpen(false)}>
          <div className="bg-white rounded-lg shadow-lg p-6 w-[400px]" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg mx-auto font-semibold">Add New Employee</h2>
              <button className="text-gray-600 hover:text-black" onClick={() => setIsAddEmployeeModalOpen(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddEmployee}>
              <input
                type="text"
                placeholder="Full Name"
                className="w-full border rounded p-2 mb-4"
                value={newEmployee.name}
                onChange={(e) => setNewEmployee({ ...newEmployee, name: e.target.value })}
                required
              />
              <input
                type="email"
                placeholder="Email"
                className="w-full border rounded p-2 mb-4"
                value={newEmployee.email}
                onChange={(e) => {
                  setNewEmployee({ ...newEmployee, email: e.target.value });
                  setAddEmployeeError("");
                }}
                required
              />
              <input
                type="text"
                placeholder="Employee ID"
                className="w-full border rounded p-2 mb-4"
                value={newEmployee.employeeID}
                onChange={(e) => setNewEmployee({ ...newEmployee, employeeID: e.target.value })}
                required
              />
              
              {addEmployeeError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-red-600 text-sm">{addEmployeeError}</p>
                </div>
              )}
              
              <button
                type="submit"
                disabled={isAddingEmployee}
                className={`text-white w-full py-2 rounded-md ${
                  isAddingEmployee 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-[#ce5841] hover:bg-orange-700'
                }`}
              >
                {isAddingEmployee ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Adding Employee...
                  </div>
                ) : (
                  'Add Employee'
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Edit Employee Modal */}
      {isEditModalOpen && editingEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-40" onClick={() => setIsEditModalOpen(false)}>
          <div className="bg-white rounded-lg shadow-lg p-6 w-[400px]" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg mx-auto font-semibold">Edit Employee</h2>
              <button className="text-gray-600 hover:text-black" onClick={() => setIsEditModalOpen(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleEditEmployee}>
              <input
                type="text"
                placeholder="Full Name"
                className="w-full border rounded p-2 mb-4"
                value={editingEmployee.name}
                onChange={(e) => setEditingEmployee({ ...editingEmployee, name: e.target.value })}
                required
              />
              <input
                type="email"
                placeholder="Email"
                className="w-full border rounded p-2 mb-4"
                value={editingEmployee.email}
                onChange={(e) => setEditingEmployee({ ...editingEmployee, email: e.target.value })}
                required
              />
              <input
                type="text"
                placeholder="Employee ID"
                className="w-full border rounded p-2 mb-4"
                value={editingEmployee.employeeID}
                onChange={(e) => setEditingEmployee({ ...editingEmployee, employeeID: e.target.value })}
                required
              />
              
              <button
                type="submit"
                disabled={updatingEmployee === editingEmployee._id}
                className={`text-white w-full py-2 rounded-md ${
                  updatingEmployee === editingEmployee._id
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-[#ce5841] hover:bg-orange-700'
                }`}
              >
                {updatingEmployee === editingEmployee._id ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Updating...
                  </div>
                ) : (
                  'Update Employee'
                )}
              </button>
            </form>
          </div>
        </div>
      )}

   {/* Bulk Upload Modal */}
{isBulkUploadModalOpen && (
  <div className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-40" onClick={() => setIsBulkUploadModalOpen(false)}>
    <div className="bg-white rounded-lg shadow-lg p-6 w-[600px]" onClick={(e) => e.stopPropagation()}>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg mx-auto font-semibold">Bulk Upload Employees</h2>
        <button className="text-gray-600 hover:text-black" onClick={() => setIsBulkUploadModalOpen(false)}>
          <X className="w-5 h-5" />
        </button>
      </div>
      
      <form onSubmit={handleExcelUpload}>
      <p className="text-sm text-gray-600 mb-2">Upload Excel file with columns in this order:</p>
<p className="text-xs text-gray-500 mb-3 font-mono bg-gray-50 p-2 rounded">
  Column 1: Employee name | Column 2: Employee ID | Column 3: Email address
</p>
        
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 mb-4 text-center">
          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <input
            type="file"
            accept=".xlsx, .xls"
            onChange={(e) => {
              setExcelFile(e.target.files[0]);
              setAddEmployeeError("");
            }}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-[#ce5841] file:text-white hover:file:bg-orange-700 cursor-pointer"
            required
          />
          {excelFile && (
            <p className="text-sm text-gray-600 mt-2">Selected: {excelFile.name}</p>
          )}
        </div>
        
        {addEmployeeError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-sm">{addEmployeeError}</p>
          </div>
        )}
        
        <button
          type="submit"
          disabled={isAddingEmployee}
          className={`text-white w-full py-2 rounded-md ${
            isAddingEmployee 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-[#ce5841] hover:bg-orange-700'
          }`}
        >
          {isAddingEmployee ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Uploading...
            </div>
          ) : (
            'Upload Employees'
          )}
        </button>
      </form>
    </div>
  </div>
)}

      {/* Main Content */}
      <div className="ml-32 mr-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-gray-900 flex items-center">
                Manage Employees
              </h1>
            </div>
            <div className="flex gap-4">
            <button
    onClick={handleExportToExcel}
    className="bg-transparent text-green-700 px-6 py-2 rounded-lg font-medium border-green-600 border-2 transition-colors flex items-center gap-2"
  >
    <Download className="w-4 h-4" /> Export
  </button>
              <button
                onClick={() => setIsBulkUploadModalOpen(true)}
                className="bg-transparent text-[#ce5841] border-2 border-[#ce5841] px-6 py-1 rounded-lg font-medium hover:bg-[#ce5841] hover:text-white transition-colors flex items-center gap-2"
              >
                <Upload className="w-4 h-4" /> Bulk Upload
              </button>
              <button
                onClick={() => setIsAddEmployeeModalOpen(true)}
                className="bg-[#ce5841] text-white px-6 py-2 rounded-lg font-medium hover:bg-orange-700 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> Add Employee
              </button>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        {successMessage && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start">
            <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
            <span className="text-green-700">{successMessage}</span>
          </div>
        )}

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by name, email, or employee ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent"
            />
          </div>
        </div>

        {/* Employees Table */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee ID
                  </th>
                
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEmployees.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                      {searchQuery ? 'No employees found matching your search.' : 'No employees found.'}
                    </td>
                  </tr>
                ) : (
                  filteredEmployees.map((employee) => (
                    <tr key={employee._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full bg-[#ce5841] flex items-center justify-center text-white font-semibold mr-3">
                            {employee.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {employee.name}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center">
                              <Mail className="w-3 h-3 mr-1" />
                              {employee.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <Hash className="w-3 h-3 mr-1 text-gray-400" />
                          {employee.employeeID}
                        </div>
                      </td>
                   
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          {new Date(employee.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setEditingEmployee(employee);
                              setIsEditModalOpen(true);
                            }}
                            className="p-2 text-[#ce5841] hover:bg-orange-50 rounded-lg transition-colors"
                            title="Edit employee"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteEmployee(employee._id, employee.name)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete employee"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6 text-sm text-gray-500 text-center">
          Showing {filteredEmployees.length} of {employees.length} employees
        </div>
      </div>
    </div>
  );
};

export default ManageEmployees;